'use client';

/**
 * Client error reporting.
 *
 * Failures a user can act on are handled in-product (toasts, empty states).
 * This captures everything ELSE — uncaught exceptions, unhandled promise
 * rejections, and boundary fallbacks — and ships a sanitized, rate-limited
 * report to /api/errors.
 *
 * Design rules:
 *  - never send user data: only message, stack, route, and browser facts
 *  - de-duplicate identical reports (a render loop must not spam the log)
 *  - fire-and-forget: reporting must never break the app it is reporting on
 */

const ENDPOINT = '/api/errors';
const DEDUPE_WINDOW_MS = 10_000;
const MAX_MESSAGE = 500;

const recent = new Map<string, number>();

function fingerprint(message: string, source: string): string {
  return `${source}::${message.slice(0, 120)}`;
}

/** Rate-limit duplicate reports so a render loop can't flood the endpoint. */
function shouldSend(key: string): boolean {
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recent.set(key, now);
  // Keep the map bounded — a long session shouldn't grow it without limit.
  if (recent.size > 50) {
    for (const [k, ts] of recent) {
      if (now - ts > DEDUPE_WINDOW_MS) recent.delete(k);
    }
  }
  return true;
}

export interface ErrorReport {
  message: string;
  source: 'boundary' | 'global' | 'window' | 'promise' | 'manual';
  stack?: string;
  route?: string;
}

/**
 * Send one error report. Resolves even if the request fails — a reporting
 * outage must never escalate into an application outage.
 */
export async function reportError(report: ErrorReport): Promise<void> {
  try {
    const key = fingerprint(report.message, report.source);
    if (!shouldSend(key)) return;

    const body = JSON.stringify({
      message: String(report.message ?? 'Unknown error').slice(0, MAX_MESSAGE),
      source: report.source,
      stack: report.stack?.slice(0, 4000),
      route: report.route ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 200) : undefined,
      release: process.env.NEXT_PUBLIC_APP_VERSION ?? 'dev',
    });

    // sendBeacon survives page unload (which is when global-error fires).
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }

    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Reporting is best-effort. Swallow everything.
  }
}

/**
 * Install global listeners once (idempotent). Called from the root layout so
 * window-level failures reach /api/errors even when no boundary is involved.
 */
let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    void reportError({
      message: event.message || String(event.error ?? 'window error'),
      source: 'window',
      stack: event.error instanceof Error ? event.error.stack : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    void reportError({
      message: reason instanceof Error ? reason.message : String(reason ?? 'Unhandled rejection'),
      source: 'promise',
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });
}
